/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { CustomPixel, PixelFieldExtraction } from '../../../types';
import type { MacroExpression } from '../../../types/decorator-expression';
import { DEFAULT_PROXY_PIXEL_URL_TEMPLATE } from './pixel-template-snippet';
import type { MappingIssue } from './mapping-issues';

export interface PixelExtractionRow {
  id: string;
  field: string;
  pattern: string;
  valueType?: PixelFieldExtraction['valueType'];
  transform?: PixelFieldExtraction['transform'];
  defaultValue?: PixelFieldExtraction['defaultValue'];
  description?: string;
  /**
   * Optional decorator expression for this extraction field.
   * When set, the expression is evaluated against the request context to
   * provide the field value (or a fallback when the pattern fails to match).
   *
   * Either `pattern` or `mappingExpression` must be populated for an
   * extraction to be persisted when saving.
   */
  mappingExpression?: MacroExpression;
}

export interface PixelRow {
  id: string;
  name: string;
  urlTemplate: string;
  type: 'impression' | 'tracking' | 'custom';
  injectPixel: boolean;
  description: string;
  adServerType: string;
  extractions: PixelExtractionRow[];
}

export type PixelTemplateId = 'pricing-extraction' | 'impression-beacon' | 'start-tracking' | 'complete-tracking';

export interface PixelTemplateDefinition {
  id: PixelTemplateId;
  label: string;
  description: string;
}

export const PIXEL_TEMPLATE_DEFINITIONS: PixelTemplateDefinition[] = [
  {
    id: 'pricing-extraction',
    label: 'Pricing Extraction',
    description: 'Impression pixel with CPM extraction mapping',
  },
  {
    id: 'impression-beacon',
    label: 'Impression Beacon',
    description: 'Standard bidBrain impression endpoint pixel',
  },
  {
    id: 'start-tracking',
    label: 'Start Tracking',
    description: 'Tracking pixel for video start event',
  },
  {
    id: 'complete-tracking',
    label: 'Complete Tracking',
    description: 'Tracking pixel for video complete event',
  },
];

export function createExtractionRow(partial?: Partial<PixelExtractionRow>): PixelExtractionRow {
  return {
    id: partial?.id ?? `ext-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    field: partial?.field ?? '',
    pattern: partial?.pattern ?? '',
    valueType: partial?.valueType,
    transform: partial?.transform,
    defaultValue: partial?.defaultValue,
    description: partial?.description,
    mappingExpression: partial?.mappingExpression,
  };
}

export function createPixelRow(partial?: Partial<PixelRow>): PixelRow {
  return {
    id: partial?.id ?? `pixel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: partial?.name ?? '',
    urlTemplate: partial?.urlTemplate ?? DEFAULT_PROXY_PIXEL_URL_TEMPLATE,
    type: partial?.type ?? 'impression',
    injectPixel: partial?.injectPixel ?? false,
    description: partial?.description ?? '',
    adServerType: partial?.adServerType ?? '',
    extractions: partial?.extractions ?? [],
  };
}

export function createTemplatePixelRow(partial?: Partial<PixelRow>): PixelRow {
  return createPixelTemplateRow('pricing-extraction', partial);
}

export function createPixelTemplateRow(templateId: PixelTemplateId, partial?: Partial<PixelRow>): PixelRow {
  if (templateId === 'impression-beacon') {
    return createPixelRow({
      ...partial,
      name: 'Impression Beacon',
      urlTemplate: '{{PROXY_BASE_URL}}/pixel?pid=impression&sid={sessionId}&event=impression&cb={cb}',
      type: 'impression',
      injectPixel: true,
      description: 'Standard bidBrain impression beacon',
      adServerType: 'Generic',
      extractions: [],
    });
  }

  if (templateId === 'start-tracking') {
    return createPixelRow({
      ...partial,
      name: 'Start Tracking Pixel',
      urlTemplate: '{{PROXY_BASE_URL}}/pixel?pid=start&sid={sessionId}&event=start&adId={adId}&cb={cb}',
      type: 'tracking',
      injectPixel: true,
      description: 'Track video start events through bidBrain',
      adServerType: 'Generic',
      extractions: [],
    });
  }

  if (templateId === 'complete-tracking') {
    return createPixelRow({
      ...partial,
      name: 'Complete Tracking Pixel',
      urlTemplate: '{{PROXY_BASE_URL}}/pixel?pid=complete&sid={sessionId}&event=complete&adId={adId}&cb={cb}',
      type: 'tracking',
      injectPixel: true,
      description: 'Track video completion events through bidBrain',
      adServerType: 'Generic',
      extractions: [],
    });
  }

  return createPixelRow({
    ...partial,
    name: 'Pricing Pixel',
    urlTemplate: DEFAULT_PROXY_PIXEL_URL_TEMPLATE,
    type: 'impression',
    injectPixel: false,
    description: 'Template pixel for pricing extraction',
    adServerType: 'Generic',
    extractions: [
      createExtractionRow({
        field: 'bid',
        pattern: 'cpm=([0-9.]+)',
        valueType: 'number',
        description: 'Extract bid CPM from pixel URL',
      }),
    ],
  });
}

export function dtoCustomPixelsToRows(customPixels?: CustomPixel[]): PixelRow[] {
  if (!customPixels || customPixels.length === 0) return [];
  return customPixels.map((pixel, index) => {
    const extractions = Object.entries(pixel.extractionMappings ?? {}).map(([field, value]) =>
      createExtractionRow({
        field,
        pattern: value.pattern,
        valueType: value.valueType,
        transform: value.transform,
        defaultValue: value.defaultValue,
        description: value.description,
        mappingExpression: value.expression,
      }),
    );

    return createPixelRow({
      id: pixel.id ?? `pixel-${index}`,
      name: pixel.name ?? '',
      urlTemplate: pixel.urlTemplate,
      type: pixel.type ?? 'impression',
      injectPixel: !!pixel.injectPixel,
      description: pixel.description ?? '',
      adServerType: pixel.adServerType ?? '',
      extractions,
    });
  });
}

export function rowsToDtoCustomPixels(rows: PixelRow[]): CustomPixel[] {
  return rows
    .filter((row) => row.urlTemplate.trim().length > 0)
    .map((row) => {
      const extractionMappings: Record<string, PixelFieldExtraction> = {};
      row.extractions.forEach((extraction) => {
        const key = extraction.field.trim();
        if (!key || (!extraction.pattern.trim() && !extraction.mappingExpression)) return;
        extractionMappings[key] = {
          pattern: extraction.pattern.trim(),
          valueType: extraction.valueType,
          transform: extraction.transform,
          defaultValue: extraction.defaultValue,
          description: extraction.description,
          expression: extraction.mappingExpression,
        };
      });

      return {
        id: row.id,
        name: row.name || undefined,
        urlTemplate: row.urlTemplate.trim(),
        type: row.type,
        injectPixel: row.injectPixel,
        extractionMappings: Object.keys(extractionMappings).length > 0 ? extractionMappings : undefined,
        description: row.description || undefined,
        adServerType: row.adServerType || undefined,
      };
    });
}

/**
 * Validate pixel rows for the shared warnings model (guidance §16). Levels:
 *  - `error` — a pixel with no URL template. It can't be persisted
 *    (`rowsToDtoCustomPixels` drops it) and the wizard already blocks Save on it,
 *    so it's a real blocker, not a nag.
 *  - `warn`  — an extraction that won't populate: a field name with neither a
 *    pattern nor an expression (nothing to extract with), or a pattern/expression
 *    with no field name (nowhere to store the result). Both are silently dropped
 *    by `rowsToDtoCustomPixels`, so surface them rather than lose them quietly.
 */
export function validatePixels(rows: PixelRow[]): MappingIssue[] {
  const issues: MappingIssue[] = [];
  rows.forEach((row, i) => {
    const label = row.name?.trim() || `pixel ${i + 1}`;
    if (!row.urlTemplate.trim()) {
      issues.push({ kind: 'incomplete', message: `\`${label}\` has no URL template.`, ref: label });
    }
    row.extractions.forEach((ex) => {
      const hasField = !!ex.field.trim();
      const hasRule = !!ex.pattern.trim() || !!ex.mappingExpression;
      if (hasField && !hasRule) {
        issues.push({
          kind: 'missing-value',
          message: `\`${label}\` → \`${ex.field.trim()}\` has no pattern or expression — it won't extract anything.`,
          ref: label,
        });
      } else if (!hasField && hasRule) {
        issues.push({
          kind: 'missing-value',
          message: `\`${label}\` has an extraction rule with no field name — it has nowhere to store the value.`,
          ref: label,
        });
      }
    });
  });
  return issues;
}
